import { useMutation } from "@tanstack/react-query";

import { ApiError } from "@/lib/api/client";
import { downloadCsv, type CsvType } from "@/lib/api/jobs";

const FILENAME_MAP: Record<CsvType, string> = {
  delivery: "インボイスOCR結果.csv",
  review: "インボイスOCR結果_확인용.csv",
  rejected_delivery: "返却_インボイスOCR結果.csv",
  rejected_review: "返却_インボイスOCR結果_확인용.csv",
};

interface DownloadVariables {
  jobId: string;
  type: CsvType;
}

/** content-disposition 헤더에서 RFC 5987 filename* 을 우선 파싱. */
function extractFilename(disposition: string | undefined, fallback: string): string {
  if (!disposition) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fallthrough */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1] ?? fallback;
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 다음 tick 에 revoke — 일부 브라우저(특히 Safari)에서 즉시 revoke 시 다운로드 취소
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function useDownloadCsv() {
  return useMutation<{ filename: string }, ApiError | Error, DownloadVariables>({
    mutationFn: async ({ jobId, type }) => {
      const blob = await downloadCsv(jobId, type);
      const filename = FILENAME_MAP[type];
      triggerBrowserDownload(blob, filename);
      return { filename };
    },
  });
}

export { FILENAME_MAP, extractFilename };
